import React, { useEffect, useState } from 'react';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import {
  MDBTypography,
  MDBSpinner,
  MDBRow,
  MDBCol,
  MDBBtnGroup,
  MDBBtn,
  MDBCard
} from 'mdb-react-ui-kit';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell
} from 'recharts';

function ReportTab() {
  const [inventory, setInventory] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('inventory');
  const [selectedItem, setSelectedItem] = useState('');
  const [salesView, setSalesView] = useState('date');

  const [selectedMonthA, setSelectedMonthA] = useState('');
  const [selectedYearA, setSelectedYearA] = useState('');

  const [selectedMonthB, setSelectedMonthB] = useState('');
  const [selectedYearB, setSelectedYearB] = useState('');

  const [selectedYearC, setSelectedYearC] = useState('');


  const [reportMode, setReportMode] = useState("none"); 

  const fetchData = async () => {
    setLoading(true);
    try {
      const [inventoryRes, transactionsRes, ordersRes, orderDetailsRes, customerRes, productRes] = await Promise.all([
        fetch('https://krizkhen-floral-design.onrender.com/api/inventory/get-all-inventory'),
        fetch('https://krizkhen-floral-design.onrender.com/api/transactions/get-all-transactions/'),
        fetch('https://krizkhen-floral-design.onrender.com/api/orders/get-all-orders'),
        fetch('https://krizkhen-floral-design.onrender.com/api/order-details/get-all-order-details'),
        fetch('https://krizkhen-floral-design.onrender.com/api/customers/get-all-customers'),
        fetch('https://krizkhen-floral-design.onrender.com/api/products/get-all-products'),
      ]);

      if (!inventoryRes.ok) throw new Error('Failed to fetch inventory');
      if (!transactionsRes.ok) throw new Error('Failed to fetch transactions');
      if (!ordersRes.ok) throw new Error('Failed to fetch orders');
      if (!orderDetailsRes.ok) throw new Error('Failed to fetch order details');
      if (!customerRes.ok) throw new Error('Failed to fetch customers');
      if (!productRes.ok) throw new Error('Failed to fetch products');

      const inventoryData = await inventoryRes.json();
      const transactionsData = await transactionsRes.json();
      const ordersData = await ordersRes.json();
      const orderDetailsData = await orderDetailsRes.json();
      const customersData = await customerRes.json();
      const productData = await productRes.json();

      setInventory(inventoryData);
      setTransactions(transactionsData);
      setOrders(ordersData);
      setOrderDetails(orderDetailsData);
      setCustomers(customersData);
      setProducts(productData);

      if (inventoryData.length > 0) setSelectedItem(inventoryData[0].product_no);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData()
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  if (error) {
    return (
      <MDBTypography tag="p" className="text-danger text-center mt-4">
        Error: {error}
      </MDBTypography>
    );
  }



  const handleGenerateInventoryPDF = () => {
    if (!selectedMonthA || !selectedYearA) {
      alert("Please select month and year for Inventory Report.");
      return;
    }

    const doc = new jsPDF();
    const reportDate = new Date().toLocaleDateString();
    const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
    const selectedMonthName = monthNames[selectedMonthA - 1];


    doc.setFontSize(10);
    doc.text(`Generated on ${reportDate}`, 14, 10);

    doc.setFont("helvetica", "bold");

    doc.setFontSize(12);
    doc.text("KRIZKHEN FLORAL DESIGN", 105, 25, { align: "center" }); 

    doc.setFontSize(10);
    doc.text("INVENTORY REPORT", 105, 35, { align: "center" });
    doc.text(`FOR THE MONTH OF ${selectedMonthName.toUpperCase()}`, 105, 40, { align: "center" });



    const tableData = inventory.map(item => {
      const itemTransactions = transactions.filter(t => t.product_no === item.product_no);

      const lastMonthQty = item.total_qty;
      const lastMonthAmt = lastMonthQty * item.unit_cost;

      const inThisMonthQty = itemTransactions
        .filter(t => {
          const date = new Date(t.date);
          return date.getMonth() + 1 === parseInt(selectedMonthA) && date.getFullYear() === parseInt(selectedYearA) && t.type === "stock_in";
        })
        .reduce((acc, t) => acc + t.quantity, 0);

      const inThisMonthAmt = inThisMonthQty * item.unit_cost;


      const outThisMonthQty = itemTransactions
        .filter(t => {
          const date = new Date(t.date);
          return date.getMonth() + 1 === parseInt(selectedMonthA) && date.getFullYear() === parseInt(selectedYearA) && t.type === "stock_out";
        })
        .reduce((acc, t) => acc + t.quantity, 0);

      const outThisMonthAmt = outThisMonthQty * item.unit_cost;


      const wastageQty = itemTransactions
        .filter(t => {
          const date = new Date(t.date);
          return date.getMonth() + 1 === parseInt(selectedMonthA) && date.getFullYear() === parseInt(selectedYearA) && t.type === "wastage";
        })
        .reduce((acc, t) => acc + t.quantity, 0);

      const wastageAmt = wastageQty * item.unit_cost;

      return [
        item.product_no,
        item.description,
        lastMonthQty,
        lastMonthAmt.toFixed(2),
        inThisMonthQty,
        inThisMonthAmt.toFixed(2),
        outThisMonthQty,
        outThisMonthAmt.toFixed(2),
        wastageQty,
        wastageAmt.toFixed(2),
      ];
    });


    const totals = tableData.reduce(
      (acc, row) => {
        acc.lastQty += row[2];
        acc.lastAmt += parseFloat(row[3]);
        acc.inQty += row[4];
        acc.inAmt += parseFloat(row[5]);
        acc.outQty += row[6];
        acc.outAmt += parseFloat(row[7]);
        acc.wastageQty += row[8];
        acc.wastageAmt += parseFloat(row[9]);
        return acc;
      },
      { lastQty: 0, lastAmt: 0, inQty: 0, inAmt: 0, outQty: 0, outAmt: 0, wastageQty: 0, wastageAmt: 0 }
    );

    tableData.push([
      "TOTAL",
      "",
      totals.lastQty,
      totals.lastAmt.toFixed(2),
      totals.inQty,
      totals.inAmt.toFixed(2),
      totals.outQty,
      totals.outAmt.toFixed(2),
      totals.wastageQty,
      totals.wastageAmt.toFixed(2),
    ]);

    autoTable(doc, {
      head: [
        [
          { content: "ITEM NO.", rowSpan: 2 },
          { content: "PRODUCT DESCRIPTION", rowSpan: 2 },
          { content: "CURRENT STOCK", colSpan: 2, styles: { halign: "center" } },
          { content: "IN THIS MONTH", colSpan: 2, styles: { halign: "center" } },
          { content: "OUT THIS MONTH", colSpan: 2, styles: { halign: "center" } },
          { content: "WASTAGE", colSpan: 2, styles: { halign: "center" } },
        ],
        [
          "QTY",
          "AMT",
          "QTY",
          "AMT",
          "QTY",
          "AMT",
          "QTY",
          "AMT",
        ]
      ],
      body: tableData,
      startY: 45,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [167, 141, 121] },
      footStyles: { fillColor: [200, 200, 200] } 
    });


    doc.save(`Inventory_Report_${selectedMonthA}_${selectedYearA}.pdf`);
  };



  const handleGenerateMonthlySalesPDF = () => {
    if (!selectedMonthB || !selectedYearB) {
      alert("Please select month and year for Monthly Sales Report.");
      return;
    }

    const doc = new jsPDF();
    const reportDate = new Date().toLocaleDateString();
    const monthNames = [
      "January","February","March","April","May","June",
      "July","August","September","October","November","December"
    ];
    const selectedMonthName = monthNames[selectedMonthB - 1];

    doc.setFontSize(10);
    doc.text(`Generated on ${reportDate}`, 14, 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("KRIZKHEN FLORAL DESIGN", 105, 22, { align: "center" });

    doc.setFontSize(12);
    doc.text("MONTHLY SALES REPORT", 105, 30, { align: "center" });

    doc.text(
      `FOR THE MONTH OF ${selectedMonthName.toUpperCase()} ${selectedYearB}`,
      105,
      36,
      { align: "center" }
    );

    const filteredOrders = orders.filter(order => {
      const date = new Date(order.order_date);
      const orderMonth = date.getMonth() + 1;
      const orderYear = date.getFullYear();

      return (
        order.status === "Completed" &&
        orderMonth === parseInt(selectedMonthB) &&
        orderYear === parseInt(selectedYearB)
      );
    });

    const tableData = filteredOrders.map(order => {
      const orderNum = order.order_no;
      const orderDate = order.order_date;
      const formattedDate = new Date(orderDate).toLocaleDateString();

      const orderTotalAmount = order.total_amount ?? 0;
      const orderType = order.type;

      var orderProductName = 'Custom Flower Arrangement'

      if (orderType == 'Fixed') {
        const orderDts = orderDetails.find(o => o.order_no === orderNum);
        const orderProduct = products.find(p => p.product_no === orderDts.product_no);
        orderProductName = orderProduct.product_name
      }


      const orderCustDts = customers.find(c => c.customer_id === order?.customer_id);
      const orderCustomerName = orderCustDts?.customer_name || "N/A";
      const orderCustomerNum = orderCustDts?.contact_no || "N/A";

      return [
        formattedDate,
        orderNum,
        orderCustomerName,
        orderCustomerNum,
        orderProductName,
        orderType,
        orderTotalAmount.toFixed(2),
      ];
    });

    const totals = tableData.reduce(
      (acc, row) => {
        acc.orderTotalAmount += parseFloat(row[6]);
        return acc;
      },
      { orderTotalAmount: 0 }
    );

    tableData.push([
      "",
      "",
      "",
      "",
      "",
      "TOTAL",
      totals.orderTotalAmount.toFixed(2),
    ]);

    autoTable(doc, {
      head: [
        [
          "ORDER DATE",
          "ORDER NO.",
          "CUSTOMER NAME",
          "CONTACT NO.",
          "ORDER DESCRIPTION.",
          "TYPE",
          "TOTAL",
        ],
      ],
      body: tableData,
      startY: 45,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [167, 141, 121] },
      footStyles: { fillColor: [200, 200, 200] }
    });


    const finalY = doc.lastAutoTable.finalY + 10;

    let fixedCount = 0;
    let customCount = 0;
    let fixedTotal = 0;
    let customTotal = 0;

    filteredOrders.forEach(order => {
      const amount = parseFloat(order.total_amount ?? 0);

      if (order.type === "Fixed") {
        fixedCount++;
        fixedTotal += amount;
      } else {
        customCount++;
        customTotal += amount;
      }
    });

    const summaryData = [
      ["ORDER TYPE", "COUNT", "TOTAL AMOUNT"],
      ["Fixed", fixedCount, fixedTotal.toFixed(2)],
      ["Custom", customCount, customTotal.toFixed(2)],
    ];

    autoTable(doc, {
      head: [summaryData[0]],
      body: summaryData.slice(1),
      startY: finalY,
      styles: { fontSize: 8 },
      margin: { left: 110 }, 
      headStyles: { fillColor: [167, 141, 121] }
    });


    doc.save(`Monthly_Sales_Report_${selectedMonthB}_${selectedYearB}.pdf`);
  };



  const handleGenerateAnnualSalesPDF = () => {
    if (!selectedYearC) {
      alert("Please select a year for Annual Sales Report.");
      return;
    }

    const doc = new jsPDF();
    const reportDate = new Date().toLocaleDateString();

    const monthNames = [
      "JANUARY","FEBRUARY","MARCH","APRIL","MAY","JUNE",
      "JULY","AUGUST","SEPTEMBER","OCTOBER","NOVEMBER","DECEMBER"
    ];


    doc.setFontSize(10);
    doc.text(`Generated on ${reportDate}`, 14, 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("KRIZKHEN FLORAL DESIGN", 105, 22, { align: "center" });

    doc.setFontSize(12);
    doc.text("ANNUAL SALES SUMMARY REPORT", 105, 30, { align: "center" });

    doc.text(`${selectedYearC}`, 105, 36, { align: "center" });


    const rows = [];
    let totalFixedQty = 0, totalFixedSales = 0;
    let totalCustomQty = 0, totalCustomSales = 0;

    monthNames.forEach((monthName, index) => {
      const monthNum = index + 1;

      const monthlyOrders = orders.filter(o => {
        const dt = new Date(o.order_date);
        return (
          o.status === "Completed" &&          
          (dt.getMonth() + 1) === monthNum &&
          dt.getFullYear() === parseInt(selectedYearC)
        );
      });

      let fixedQty = 0, fixedSales = 0;
      let customQty = 0, customSales = 0;

      monthlyOrders.forEach(order => {
        const amount = parseFloat(order.total_amount ?? 0);

        if (order.type === "Fixed") {
          fixedQty++;
          fixedSales += amount;
        } else {
          customQty++;
          customSales += amount;
        }
      });

      totalFixedQty += fixedQty;
      totalFixedSales += fixedSales;
      totalCustomQty += customQty;
      totalCustomSales += customSales;

      const monthlyTotalSales = fixedSales + customSales;

      rows.push([
        monthName,
        fixedQty,
        fixedSales.toLocaleString(undefined, { minimumFractionDigits: 2 }),
        customQty,
        customSales.toLocaleString(undefined, { minimumFractionDigits: 2 }),
        monthlyTotalSales.toLocaleString(undefined, { minimumFractionDigits: 2 })
      ]);
    });


    const grandTotal = totalFixedSales + totalCustomSales;

    rows.push([
      "TOTAL",
      totalFixedQty,
      totalFixedSales.toLocaleString(undefined, { minimumFractionDigits: 2 }),
      totalCustomQty,
      totalCustomSales.toLocaleString(undefined, { minimumFractionDigits: 2 }),
      grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })
    ]);


    autoTable(doc, {
      head: [
        [
          "MONTH",
          { content: "FIXED ORDERS", colSpan: 2, styles: { halign: "center" } },
          { content: "CUSTOM ORDERS", colSpan: 2, styles: { halign: "center" } },
          "TOTAL"
        ],
        ["", "QTY", "SALES", "QTY", "SALES", ""]
      ],

      body: rows,
      startY: 45,

      styles: { fontSize: 8 },
      headStyles: { fillColor: [167, 141, 121] },

      didParseCell: function (data) {
        const lastRowIndex = rows.length - 1;

        if (data.section === "body" && data.row.index === lastRowIndex) {
          data.cell.styles.fillColor = [167, 141, 121]; 
          data.cell.styles.textColor = [255, 255, 255]; 
          data.cell.styles.fontStyle = "bold";
        }
      }
    });

    doc.save(`Annual_Sales_Report_${selectedYearC}.pdf`);
  };





  if (reportMode === "none") {
    return (
      <div className="mt-4 w-100">
        <MDBRow className="align-items-center mb-4">
          <MDBCol>
            <MDBTypography tag="h1" className="custom-font" style={{ color: '#A78D79' }}>
              Reports
            </MDBTypography>
          </MDBCol>

          <MDBCol md="auto" className="ms-auto">
            <MDBBtn
              style={{ backgroundColor: '#A78D79', color: 'white' }}
              onClick={() => setReportMode("graphics")}
            >
              View Graphic Display
            </MDBBtn>
          </MDBCol>
        </MDBRow>



        <MDBCard className="w-100 mb-4 p-4" style={{  backgroundColor: 'rgba(245, 245, 245, 1)'}}>
          <MDBTypography tag="h4" className="fw-bold mb-3" style={{ color: '#6F5541' }}>
            Generate Monthly Inventory Report
          </MDBTypography>

          <MDBRow className="align-items-center">
            <MDBCol md="4" className="mb-3">
              <MDBTypography className="fw-bold mb-1">Select Month</MDBTypography>
              <select
                className="form-select"
                value={selectedMonthA}
                onChange={(e) => setSelectedMonthA(e.target.value)}
              >
                <option value="">-- Month --</option>
                {[
                  "January","February","March","April","May","June",
                  "July","August","September","October","November","December",
                ].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </MDBCol>

            <MDBCol md="4" className="mb-3">
              <MDBTypography className="fw-bold mb-1">Select Year</MDBTypography>
              <select
                className="form-select"
                value={selectedYearA}
                onChange={(e) => setSelectedYearA(e.target.value)}
              >
                <option value="">-- Year --</option>
                {Array.from({ length: 6 }).map((_, idx) => {
                  const year = 2025 - idx;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </MDBCol>

            <MDBCol md="4" className="text-end">
              <MDBBtn
                color="success"
                onClick={handleGenerateInventoryPDF}
              >
                Generate PDF Report
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBCard>



        <MDBCard className="w-100 mb-4 p-4" style={{  backgroundColor: 'rgba(245, 245, 245, 1)' }}>
          <MDBTypography tag="h4" className="fw-bold mb-3" style={{ color: '#6F5541' }}>
            Generate Monthly Sales Report 
          </MDBTypography>

          <MDBRow className="align-items-center">
            <MDBCol md="4" className="mb-3">
              <MDBTypography className="fw-bold mb-1">Select Month</MDBTypography>
              <select
                className="form-select"
                value={selectedMonthB}
                onChange={(e) => setSelectedMonthB(e.target.value)}
              >
                <option value="">-- Month --</option>
                {[
                  "January","February","March","April","May","June",
                  "July","August","September","October","November","December",
                ].map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </MDBCol>

            <MDBCol md="4" className="mb-3">
              <MDBTypography className="fw-bold mb-1">Select Year</MDBTypography>
              <select
                className="form-select"
                value={selectedYearB}
                onChange={(e) => setSelectedYearB(e.target.value)}
              >
                <option value="">-- Year --</option>
                {Array.from({ length: 6 }).map((_, idx) => {
                  const year = 2025 - idx;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </MDBCol>

            <MDBCol md="4" className="text-end">
              <MDBBtn
                color="success"
                onClick={handleGenerateMonthlySalesPDF}
              >
                Generate PDF Report
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBCard>



        <MDBCard className="w-100 mb-4 p-4" style={{  backgroundColor: 'rgba(245, 245, 245, 1)' }}>
          <MDBTypography tag="h4" className="fw-bold mb-3" style={{ color: '#6F5541' }}>
            Generate Annual Sales Report
          </MDBTypography>

          <MDBRow className="align-items-center">
            <MDBCol md="6" className="mb-3">
              <MDBTypography className="fw-bold mb-1">Select Year</MDBTypography>
              <select
                className="form-select"
                value={selectedYearC}
                onChange={(e) => setSelectedYearC(e.target.value)}
              >
                <option value="">-- Select Year --</option>
                {Array.from({ length: 6 }).map((_, idx) => {
                  const year = 2025 - idx;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </MDBCol>

            <MDBCol md="6" className="text-end">
              <MDBBtn
                color="success"
                onClick={handleGenerateAnnualSalesPDF}
              >
                Generate PDF Report
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBCard>
      </div>
    );
  }



  const inventoryData = inventory.map((item) => {
    const itemTransactions = transactions.filter(t => t.product_no === item.product_no);
    const stockIn = itemTransactions.filter(t => t.type === 'stock_in').reduce((acc, t) => acc + t.quantity, 0);
    const stockOut = itemTransactions.filter(t => t.type === 'stock_out').reduce((acc, t) => acc + t.quantity, 0);
    const wastage = itemTransactions.filter(t => t.type === 'wastage').reduce((acc, t) => acc + t.quantity, 0);
    const reserved = item.reserved_qty || 0;
    const beginningBalance = item.total_qty - stockIn + stockOut + wastage;

    return {
      name: `${item.description} (${item.product_no})`,
      'Beginning Balance': beginningBalance,
      'Stock In': stockIn,
      'Stock Out': stockOut,
      'Reserved': reserved,
      'Wastage': wastage
    };
  });

  const itemStockMovementData = (() => {
    if (!selectedItem) return [];
    const filtered = transactions.filter(t => t.product_no === selectedItem);
    const grouped = {};
    filtered.forEach(t => {
      const date = new Date(t.date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      if (!grouped[date]) grouped[date] = { date, stock_in: 0, stock_out: 0 };
      if (t.type === 'stock_in') grouped[date].stock_in += t.quantity;
      if (t.type === 'stock_out') grouped[date].stock_out += t.quantity;
    });
    return Object.values(grouped).sort((a, b) => new Date(a.date) - new Date(b.date));
  })();

  const salesChartData = (() => {
    if (!orders.length || !orderDetails.length) return [];

    if (salesView === 'date') {
      const byDate = {};
      orders.forEach(order => {
        const date = new Date(order.order_date).toLocaleDateString();
        const details = orderDetails.filter(d => d.order_no === order.order_no);
        const total = details.reduce((sum, d) => sum + d.subtotal, 0);
        byDate[date] = (byDate[date] || 0) + total;
      });
      return Object.entries(byDate).map(([date, totalSales]) => ({ date, totalSales }));
    }

    if (salesView === 'type') {
      const byType = {};
      orders.forEach(order => {
        const details = orderDetails.filter(d => d.order_no === order.order_no);
        const total = details.reduce((sum, d) => sum + d.subtotal, 0);
        byType[order.type] = (byType[order.type] || 0) + total;
      });
      return Object.entries(byType).map(([type, totalSales]) => ({ type, totalSales }));
    }

    if (salesView === 'total') {
      return orders.map(order => {
        const details = orderDetails.filter(d => d.order_no === order.order_no);
        const totalSales = details.reduce((sum, d) => sum + d.subtotal, 0);
        return {
          order_no: order.order_no,
          totalSales,
          date: new Date(order.order_date).toLocaleDateString(),
          type: order.type
        };
      });
    }

    return [];
  })();

  return (
    <div className="mt-4">

      <MDBRow className="align-items-center mb-4">
        <MDBCol>
          <MDBTypography tag="h1" className="custom-font" style={{ color: '#A78D79' }}>
            Graphical Reports
          </MDBTypography>
        </MDBCol>

        <MDBCol className="d-flex justify-content-end align-items-center">
          <MDBBtn style={{ backgroundColor: '#A78D79', color: 'white' }} onClick={() => setReportMode("none")}>
            Back to Report Options
          </MDBBtn>
        </MDBCol>
      </MDBRow>

      <MDBRow className="align-items-center mb-4">
        <MDBCol className="d-flex justify-content-end align-items-center">
          <MDBBtnGroup>
            <MDBBtn style={activeTab === 'inventory' ? { backgroundColor: '#A78D79', color: 'white' } : { backgroundColor: 'white', color: '#000' }} onClick={() => setActiveTab('inventory')} > Inventory Report </MDBBtn>
            <MDBBtn style={activeTab === 'sales' ? { backgroundColor: '#A78D79', color: 'white' } : { backgroundColor: 'white', color: '#000' }} onClick={() => setActiveTab('sales')} > Sales Report </MDBBtn>
            <MDBBtn style={activeTab === 'stock' ? { backgroundColor: '#A78D79', color: 'white' } : { backgroundColor: 'white', color: '#000' }} onClick={() => setActiveTab('stock')} > Stock Movement Report </MDBBtn>
          </MDBBtnGroup>
        </MDBCol>
      </MDBRow>

      {activeTab === 'inventory' && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBTypography tag="h4" className="text-center mb-3 custom-font">Inventory Report</MDBTypography>
            <ResponsiveContainer width="100%" height={600}>
              <BarChart data={inventoryData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Beginning Balance" fill="#8884d8" />
                <Bar dataKey="Stock In" fill="#82ca9d" />
                <Bar dataKey="Stock Out" fill="#ffc658" />
                <Bar dataKey="Reserved" fill="#ff8042" />
                <Bar dataKey="Wastage" fill="#d0ed57" />
              </BarChart>
            </ResponsiveContainer>
          </MDBCol>
        </MDBRow>
      )}

      {activeTab === 'sales' && (
        <MDBRow className="mb-4">
          <MDBTypography tag="h4" className="text-center mb-3 custom-font">Sales Report</MDBTypography>
          <MDBCol className="mb-3 text-end">
            <select className="form-select w-auto d-inline-block ms-2"
              value={salesView} onChange={(e) => setSalesView(e.target.value)}>
              <option value="date">Date</option>
              <option value="type">Type</option>
              <option value="total">Total Sales (individual orders)</option>
            </select>
          </MDBCol>

          <ResponsiveContainer width="100%" height={600}>
            {salesView === 'total' ? (
              <BarChart data={salesChartData}>
                <XAxis dataKey="order_no" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalSales" name="Total Sales" />
              </BarChart>
            ) : (
              <BarChart data={salesChartData}>
                <XAxis dataKey={salesView === 'date' ? 'date' : 'type'} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="totalSales" name="Total Sales" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </MDBRow>
      )}

      {activeTab === 'stock' && (
        <MDBRow className="mb-4">
          <MDBTypography tag="h4" className="text-center mb-3 custom-font">Stock Movement Report</MDBTypography>
          <MDBCol className="mb-3 text-end">
            <select className="form-select w-auto d-inline-block ms-2"
              value={selectedItem} onChange={e => setSelectedItem(e.target.value)}>
              {inventory.map(item => (
                <option key={item.product_no} value={item.product_no}>
                  {item.description} ({item.product_no})
                </option>
              ))}
            </select>
          </MDBCol>

          <ResponsiveContainer width="100%" height={600}>
            <LineChart data={itemStockMovementData}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line dataKey="stock_in" stroke="#82ca9d" />
              <Line dataKey="stock_out" stroke="#ff8042" />
            </LineChart>
          </ResponsiveContainer>
        </MDBRow>
      )}
    </div>
  );
}

export default ReportTab;
